import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useQuizApi } from "../hooks/useQuizApi";
import "../styles/process-result.style.css";
import "../styles/thank-you.style.css";
import mascotReport from "../assets/images/mascot-report.png";
import mascotResultShadow from "../assets/images/main-shadow.png";
import ThankYouHairRight from "../assets/images/thank-hair-red.png";
import ThankYouHairLeft from "../assets/images/thank-hair-red-2.png";
import mascotStarPink from "../assets/images/star-pink.svg";
import mascotStarGreen from "../assets/images/star-green.svg";
import mascotHairYellowStar from "../assets/images/thank-yellow.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useReportApi } from "../hooks/useReportApi";
import { trackReportEvent, trackUserJourney, trackError } from "../utils/ga4";
const ThankYouPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { submitQuiz, isSubmitting, submissionError } = useQuizApi();
  const { generateReport, isGenerating, generationError } = useReportApi();
  const [email, setEmail] = useState("");
  const [quizData, setQuizData] = useState<any>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Load quiz data from sessionStorage when component mounts
  useEffect(() => {
    const storedQuizData = sessionStorage.getItem("quizData");
    if (storedQuizData) {
      try {
        const parsedData = JSON.parse(storedQuizData);
        setQuizData(parsedData);
        console.log("📋 Quiz data loaded:", parsedData);
      } catch (error) {
        console.error("❌ Failed to parse quiz data:", error);
        // Redirect back to quiz if no valid data
        navigate("/quiz");
      }
    } else {
      // Redirect back to quiz if no data
      navigate("/quiz");
    }
  }, [navigate]);

  // Handle email submission
  const handleSubmitEmail = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // simple email validation
    if (!email.trim()) {
      alert("Please enter your email address");
      return;
    }

    if (!emailRegex.test(email.trim())) {
      alert("Please enter a valid email address");
      return;
    }

    if (!quizData) {
      alert("No quiz data found. Please try again.");
      return;
    }

    try {
      console.log("🚀 Submitting quiz with email:", email);

      // Prepare data for API submission
      const submissionData = {
        answers: quizData.answers,
        userInfo: {
          email: email.trim(),
          submittedAt: quizData.submittedAt,
          timeElapsed: quizData.timeElapsed || 0,
          timeFormatted: quizData.timeFormatted || "00:00",
        },
        sessionId: quizData.sessionId,
      };

      // Submit to API
      const result = await submitQuiz(
        submissionData.answers,
        submissionData.userInfo,
        submissionData.sessionId
      );

      console.log("✅ Quiz submitted successfully:", result);

      // Show processing modal for report generation
      setIsGeneratingReport(true);

      try {
        // Track report generation start
        trackReportEvent('report_generation_started', result.documentId);
        trackUserJourney('report_generation_initiated', {
          document_id: result.documentId,
          user_email: submissionData.userInfo?.email
        });

        const genReport = await generateReport(
          result.documentId,
          submissionData.userInfo
        );

        console.log("✅ Report generated successfully:", genReport);
        
        // Track successful report generation
        trackReportEvent('report_generated_successfully', genReport.reportId, genReport.reportData?.persona?.name);
        trackUserJourney('report_generation_completed', {
          report_id: genReport.reportId,
          persona: genReport.reportData?.persona?.name,
          document_id: result.documentId
        });

        // Clear session storage
        sessionStorage.removeItem("quizData");

        // Navigate to result test page with report data
        setTimeout(() => {
          navigate("/result", {
            state: {
              documentId: result.documentId,
              reportData: genReport,
            },
          });
        }, 2000);
      } catch (reportError) {
        console.error("❌ Failed to generate report:", reportError);
        
        // Track report generation error
        trackError('report_generation_failed', reportError instanceof Error ? reportError.message : 'Unknown error', {
          document_id: result.documentId,
          user_email: submissionData.userInfo?.email
        });
        
        setIsGeneratingReport(false);
        alert("Failed to generate report. Please try again.");
      }
    } catch (error) {
      console.error("❌ Failed to submit quiz:", error);
      
      // Track quiz submission error
      trackError('quiz_submission_failed', error instanceof Error ? error.message : 'Unknown error', {
        user_email: submissionData.userInfo?.email
      });
      
      alert("Failed to submit quiz. Please try again.");
    }
  };

  return (
    <div className="container">
      <div className="container-fluid-process-result">
        <div className="process-result-page">
          <div className="thank-you-content">
            <div className="mascot-control-process-result">
              <img
                src={mascotReport}
                alt="mascotResultLoading"
                className="mascot-img-process-result"
              />
              <img
                src={mascotResultShadow}
                className="mascot-img-process-result-shadow"
              />
              <img
                src={ThankYouHairLeft}
                alt="mascotHairLeft"
                className="mascot-img-thank-hair-left"
              />
              <img
                src={ThankYouHairRight}
                alt="mascotHairRight"
                className="mascot-img-thank-hair-right"
              />
              <img
                src={mascotStarPink}
                alt="mascotStarPink"
                className="mascot-img-process-result-star-pink"
              />
              <img
                src={mascotStarGreen}
                alt="mascotStarGreen"
                className="mascot-img-thank-star-green"
              />
              <img
                src={mascotHairYellowStar}
                alt="mascotHairYellow"
                className="mascot-img-thank-hair-yellow"
              />
            </div>
            <div className="thank-text-control">{t("thankYou.text")}</div>

            {/* Error Display */}
            {submissionError && (
              <div
                className="error-message"
                style={{
                  background: "#ff4444",
                  color: "white",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  fontFamily: "var(--fontLineSeed)",
                  textAlign: "center",
                }}
              >
                ❌ {submissionError}
              </div>
            )}
            <input
              type="email"
              className="thank-you-input-control"
              placeholder="Your Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              required
              pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
            />
            <button
              className="thank-you-button"
              onClick={handleSubmitEmail}
              disabled={isSubmitting || !email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())}
              style={{
                opacity:
                  isSubmitting || !email.trim() ||
                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
                    ? 0.5
                    : 1,
                cursor:
                  isSubmitting || !email.trim() ||
                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
              <FontAwesomeIcon
                icon={faArrowRight}
                className="thank-you-button-icon"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Processing Modal for Report Generation */}
      {(isGeneratingReport || isGenerating) && (
        <div className="processing-modal-overlay">
          <div className="processing-modal">
            <div className="processing-modal-content">
              <div className="processing-spinner">
               <img src={mascotReport} alt="mascotLoading" className="mascot-img-process-result" />
              </div>
              <h3>Generating Your AI Readiness Report</h3>
              <p>
              Please wait while we analyze your responses and create your personalized report <br></br>
              (this may take up to 1 minute)
              </p>
              {/* <div className="processing-steps">
                <div className="processing-step active">
                  <span className="step-icon">✓</span>
                  <span>Quiz submitted successfully</span>
                </div>
                <div
                  className={`processing-step ${isGenerating ? "active" : ""}`}
                >
                  <div className="step-progress-bar">
                    <div
                      className={`progress-fill ${
                        isGenerating ? "animate" : ""
                      }`}
                    ></div>
                  </div>
                  <span>Analyzing your responses</span>
                </div>
                <div
                  className={`processing-step ${isGenerating ? "active" : ""}`}
                >
                  <div className="step-progress-bar">
                    <div
                      className={`progress-fill ${
                        isGenerating ? "animate" : ""
                      }`}
                    ></div>
                  </div>
                  <span>Generating personalized report</span>
                </div>
              </div> */}
              {generationError && (
                <div
                  className="error-message"
                  style={{
                    background: "#ff4444",
                    color: "white",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    marginTop: "20px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    fontFamily: "var(--fontLineSeed)",
                    textAlign: "center",
                  }}
                >
                  ❌ {generationError}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThankYouPage;
